import Web3 from "web3";
import toastr from "toastr";
export const factoryAbi = require("./factoryAbi.json");
export const presaleAbi = require("./presaleAbi.json");
export const erc20Abi = require("./erc20Abi.json");
export const MAXUNIT256 =
  "115792089237316195423570985008687907853269984665640564039457584007913129639935";

// BscTestnet:
// Busd https://testnet.bscscan.com/address/0xec828b4305be12B9B3E8F584FCE8ACDCc56c86E7
// TokenX https://testnet.bscscan.com/address/0x95FB36223A312c7fB3Bb05415b1D85771A781Db2
// LpTokenX https://testnet.bscscan.com/address/0xD0C8BD31f616837C912c9Aa097B8cE45624Da987
// Presale https://testnet.bscscan.com/address/0x5e63A8a6EB456c8Eb59b8C66b8080aa92f834dF7
// PresaleFactory https://testnet.bscscan.com/address/0x6027047FF77Ea07f3490A51108675A71EbeB72De
// Locker https://testnet.bscscan.com/address/0x3e06b6886955343c25e23467CaF23d001F672E78
//
//=========================

// 2021-10-25
// PresaleFactory https://testnet.bscscan.com/address/0x83A23e185f4cb84e6056AA6C34fcE68dfaB53Ba4

export interface IConfiguration {
  rpcUrl: string;
  explorerUrl: string;
  name: string;
  chainId: number;
  chainIdHex: string;
  factory: string;
}

const TestnetConfig: IConfiguration = {
  rpcUrl: "https://data-seed-prebsc-1-s1.binance.org:8545",
  explorerUrl: "https://testnet.bscscan.com",
  name: "BSC Testnet",
  chainId: 97,
  chainIdHex: "0x61",
  factory: "0x83A23e185f4cb84e6056AA6C34fcE68dfaB53Ba4",
};

const MainnetConfig: IConfiguration = {
  rpcUrl: "https://bsc-dataseed.binance.org/",
  explorerUrl: "https://bscscan.com",
  name: "BSC Mainnet",
  chainId: 56,
  chainIdHex: "0x38",
  factory: "0xc5c5032BC1f189EFB76EeE8F920AF7c666e0F1c2",
};

export const Configurations = {
  testnetConfig: () => TestnetConfig,
  mainnetConfig: () => MainnetConfig,
};

export default class LaunchPad {
  web3: Web3;
  config: IConfiguration;
  factoryContract: any;
  constructor(config: IConfiguration) {
    this.config = config;
    this.web3 = new Web3(config.rpcUrl);
    console.log(config.rpcUrl);
    this.factoryContract = new this.web3.eth.Contract(
      factoryAbi,
      config.factory
    );
  }
  createErc20 = async (
    tokenX: string,
    rate: number,
    softCap: number,
    hardCap: number,
    whitelistAddresses: string[],
    presaleMediaLinks: string,
    bnbLimits: number[],
    description: string,
    presaleOpenAt: number,
    presaleCloseAt: number,
    provider: any,
    onHash: (hash: any) => void,
    onConfirmation: (confirmationNumber: number, receipt: {}) => void,
    onError: (error: {}, receipt: {}) => void
  ) => {
    const web3 = this.web3;
    const acc = await provider.request({
      method: "eth_requestAccounts",
    });
    console.log("acc", acc);
    const presale = new web3.eth.Contract(factoryAbi, this.config.factory);
    try {
      await presale.methods
        .createERC20Presale([
          tokenX,
          web3.utils.toWei(web3.utils.toBN(rate)),
          web3.utils.toWei(web3.utils.toBN(softCap)),
          web3.utils.toWei(web3.utils.toBN(hardCap)),
          whitelistAddresses,
          presaleMediaLinks,
          bnbLimits,
          description,
          presaleOpenAt,
          presaleCloseAt,
        ])
        .send({
          from: (await web3.eth.getAccounts())[0],
          gas: 4041157,
          gasPrice: web3.utils.toWei("0.00000001"),
        })
        .on("hash", (hash: any) => onHash(hash))
        .on("confirmation", (confirmationNumber: number, receipt: {}) =>
          onConfirmation(confirmationNumber, receipt)
        )
        .on("error", (error: {}, receipt: {}) => onError(error, receipt));
    } catch (e) {
      console.log("utils catch", e);
      return toastr.error("Transaction has been failed");
    }
  };
  getLastPresaleIndex = async () => {
    return await this.factoryContract.methods.lastPresaleIndex().call();
  };
  getPresale = async (presaleIndex: number) => {
    return await this.factoryContract.methods.presales(presaleIndex).call();
  };
  getTokenSymbol = async (tokenAddress: string) => {
    return await this.factoryContract.methods
      .getTokenSymbol(tokenAddress)
      .call();
  };
  getTokenName = async (tokenAddress: string) => {
    return await this.factoryContract.methods.getTokenName(tokenAddress).call();
  };
  fetch = async (startIndex: number, length: number) => {
    return await this.factoryContract.methods
      .getPresales(startIndex, length)
      .call();
  };
  getPresaleMediaLinks = async (presaleAddress: string) => {
    return this.factoryContract.methods
      .getPresaleMediaLinks(presaleAddress)
      .call();
  };
  getPresaleDescription = async (presaleAddress: string) => {
    const presale = new this.web3.eth.Contract(presaleAbi, presaleAddress);
    return await presale.methods.getDescription().call();
  };
  getPresaleDetails = async (presaleAddress: string) => {
    const presaleDetails = await this.factoryContract.methods
      .getPresaleDetails(presaleAddress)
      .call();
    const details = {
      presaleAddress: presaleAddress,
      token: "",
      raisedFunds: this.web3.utils.toBN(0),
      rate: this.web3.utils.toBN(0),
      amountTokenToHold: this.web3.utils.toBN(0),
      tier: this.web3.utils.toBN(0),
      presaleOpenAt: this.web3.utils.toBN(0),
      presaleCloseAt: this.web3.utils.toBN(0),
      minBNB: this.web3.utils.toBN(0),
      maxBNB: this.web3.utils.toBN(0),
      softCap: this.web3.utils.toBN(0),
      hardCap: this.web3.utils.toBN(0),
      tokenTotalSupply: this.web3.utils.toBN(0),
      tokenDecimals: this.web3.utils.toBN(0),
      participants: this.web3.utils.toBN(0),
      isApproved: false,
      onlyWhitelistedAllowed: false,
      isDistributionMode: false,
      isCanceled: false,
      isDelayed: false,
      isRefundAllowed: false,
      tokenName: "",
      tokenSymbol: "",
    };
    [details.token] = presaleDetails[0];
    [
      details.raisedFunds,
      details.rate,
      details.amountTokenToHold,
      details.tier,
      details.presaleOpenAt,
      details.presaleCloseAt,
      details.minBNB,
      details.maxBNB,
      details.softCap,
      details.hardCap,
      details.tokenTotalSupply,
      details.tokenDecimals,
      details.participants,
    ] = presaleDetails[1];
    [
      details.isApproved,
      details.onlyWhitelistedAllowed,
      details.isDistributionMode,
      details.isCanceled,
      details.isDelayed,
      details.isRefundAllowed,
    ] = presaleDetails[2];
    return details;
  };
  disableWhitelist = async (
    provider: any,
    presaleAddress: string,
    sender: string,
    onTransactionHash: (hash: string) => void,
    onConfirmation: (confirmationNumber: number, receipt: {}) => void,
    onError: (error: {}, receipt: {}) => void
  ) => {
    const web3 = new Web3(provider);
    const presale = new web3.eth.Contract(presaleAbi, presaleAddress);
    return await presale.methods
      .disableWhitelist()
      .send({ from: sender })
      .on("transactionHash", (hash: string) => onTransactionHash(hash))
      .on("confirmation", (confirmationNumber: number, receipt: {}) =>
        onConfirmation(confirmationNumber, receipt)
      )
      .on("error", (error: {}, receipt: {}) => onError(error, receipt));
  };
  isWhiteListEnabled = async (presaleAddress: string) => {
    const presale = new this.web3.eth.Contract(presaleAbi, presaleAddress);
    return await presale.methods.onlyWhitelistedAllowed().call();
  };
  getUserBNBAmount = async (presaleAddress: string, account: string) => {
    const presale = new this.web3.eth.Contract(presaleAbi, presaleAddress);
    return await presale.methods.userBNBAmount(account).call();
  };
  join = async (
    provider: any,
    presaleAddress: string,
    sender: string,
    amount: string,
    onTransactionHash: (hash: string) => void,
    onConfirmation: (confirmationNumber: number, receipt: {}) => void,
    onError: (error: {}, receipt: {}) => void
  ) => {
    const web3 = new Web3(provider);
    const presale = new web3.eth.Contract(presaleAbi, presaleAddress);
    return presale.methods
      .join()
      .send({ value: amount, from: sender })
      .on("transactionHash", (hash: string) => onTransactionHash(hash))
      .on("confirmation", (confirmationNumber: number, receipt: {}) =>
        onConfirmation(confirmationNumber, receipt)
      )
      .on("error", (error: {}, receipt: {}) => onError(error, receipt));
  };
  getRefund = async (
    provider: any,
    presaleAddress: string,
    sender: string,
    onTransactionHash: (hash: string) => void,
    onConfirmation: (confirmationNumber: number, receipt: {}) => void,
    onError: (error: {}, receipt: {}) => void
  ) => {
    const web3 = new Web3(provider);
    const presale = new web3.eth.Contract(presaleAbi, presaleAddress);
    return presale.methods
      .getRefund()
      .send({ from: sender })
      .on("transactionHash", (hash: string) => onTransactionHash(hash))
      .on("confirmation", (confirmationNumber: number, receipt: {}) =>
        onConfirmation(confirmationNumber, receipt)
      )
      .on("error", (error: {}, receipt: {}) => onError(error, receipt));
  };
  claimTokens = async (
    provider: any,
    presaleAddress: string,
    sender: string,
    onTransactionHash: (hash: string) => void,
    onConfirmation: (confirmationNumber: number, receipt: {}) => void,
    onError: (error: {}, receipt: {}) => void
  ) => {
    const web3 = new Web3(provider);
    const presale = new web3.eth.Contract(presaleAbi, presaleAddress);
    return presale.methods
      .claimTokens()
      .send({ from: sender })
      .on("transactionHash", (hash: string) => onTransactionHash(hash))
      .on("confirmation", (confirmationNumber: number, receipt: {}) =>
        onConfirmation(confirmationNumber, receipt)
      )
      .on("error", (error: {}, receipt: {}) => onError(error, receipt));
  };
  approveToken = async (
    provider: any,
    presaleAddress: string,
    tokenAddress: string,
    sender: string,
    onTransactionHash: (hash: string) => void,
    onConfirmation: (confirmationNumber: number, receipt: {}) => void,
    onError: (error: {}, receipt: {}) => void
  ) => {
    const web3 = new Web3(provider);
    const erc20 = new web3.eth.Contract(erc20Abi, tokenAddress);
    return erc20.methods
      .approve(presaleAddress, Web3.utils.toBN(MAXUNIT256))
      .send({ from: sender })
      .on("transactionHash", (hash: string) => onTransactionHash(hash))
      .on("confirmation", (confirmationNumber: number, receipt: {}) =>
        onConfirmation(confirmationNumber, receipt)
      )
      .on("error", (error: {}, receipt: {}) => onError(error, receipt));
  };
  withdrawRaisedFunds = async (
    provider: any,
    presaleAddress: string,
    sender: string,
    onTransactionHash: (hash: string) => void,
    onConfirmation: (confirmationNumber: number, receipt: {}) => void,
    onError: (error: {}, receipt: {}) => void
  ) => {
    const web3 = new Web3(provider);
    const presale = new web3.eth.Contract(presaleAbi, presaleAddress);
    return presale.methods
      .withdrawRaisedFunds()
      .send({ from: sender })
      .on("transactionHash", (hash: string) => onTransactionHash(hash))
      .on("confirmation", (confirmationNumber: number, receipt: {}) =>
        onConfirmation(confirmationNumber, receipt)
      )
      .on("error", (error: {}, receipt: {}) => onError(error, receipt));
  };
  cancelPresale = async (
    provider: any,
    presaleAddress: string,
    sender: string,
    onTransactionHash: (hash: string) => void,
    onConfirmation: (confirmationNumber: number, receipt: {}) => void,
    onError: (error: {}, receipt: {}) => void
  ) => {
    const web3 = new Web3(provider);
    const presale = new web3.eth.Contract(presaleAbi, presaleAddress);
    return presale.methods
      .cancelPresale()
      .send({ from: sender })
      .on("transactionHash", (hash: string) => onTransactionHash(hash))
      .on("confirmation", (confirmationNumber: number, receipt: {}) =>
        onConfirmation(confirmationNumber, receipt)
      )
      .on("error", (error: {}, receipt: {}) => onError(error, receipt));
  };
  closePresale = async (
    provider: any,
    presaleAddress: string,
    sender: string,
    onTransactionHash: (hash: string) => void,
    onConfirmation: (confirmationNumber: number, receipt: {}) => void,
    onError: (error: {}, receipt: {}) => void
  ) => {
    const web3 = new Web3(provider);
    const presale = new web3.eth.Contract(presaleAbi, presaleAddress);
    return presale.methods
      .closePresale()
      .send({ from: sender })
      .on("transactionHash", (hash: string) => onTransactionHash(hash))
      .on("confirmation", (confirmationNumber: number, receipt: {}) =>
        onConfirmation(confirmationNumber, receipt)
      )
      .on("error", (error: {}, receipt: {}) => onError(error, receipt));
  };
  enableDistributionMode = async (
    provider: any,
    presaleAddress: string,
    sender: string,
    onTransactionHash: (hash: string) => void,
    onConfirmation: (confirmationNumber: number, receipt: {}) => void,
    onError: (error: {}, receipt: {}) => void
  ) => {
    const web3 = new Web3(provider);
    const presale = new web3.eth.Contract(presaleAbi, presaleAddress);
    return presale.methods
      .enableDistributionMode()
      .send({ from: sender })
      .on("transactionHash", (hash: string) => onTransactionHash(hash))
      .on("confirmation", (confirmationNumber: number, receipt: {}) =>
        onConfirmation(confirmationNumber, receipt)
      )
      .on("error", (error: {}, receipt: {}) => onError(error, receipt));
  };
  getPresaleRaisedFunds = async (presaleAddress: string) => {
    const presale = new this.web3.eth.Contract(presaleAbi, presaleAddress);
    return presale.methods.raisedFunds().call();
  };
  getPresaleTokenBalance = async (
    presaleAddress: string,
    tokenAddress: string
  ) => {
    const erc20 = new this.web3.eth.Contract(erc20Abi, tokenAddress);
    return erc20.methods.balanceOf(presaleAddress).call();
  };
  onPresaleEvent = async (
    provider: any,
    event: string,
    presaleAddress: string,
    callback: (error: any, transfers: any) => void
  ) => {
    const web3 = new Web3(provider);
    const presale = new web3.eth.Contract(
      presaleAbi,
      Web3.utils.toChecksumAddress(presaleAddress)
    );
    return presale.events[event](
      {
        fromBlock: "latest",
      },
      callback
    );
  };
}
